import { ChangeDetectionStrategy, Component, input, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { categoryDefinition, isLandLed } from '../config/ci-options.config';
import { PropertyCategory, PropertyForm } from '../models/ci-form.model';
import { InventoryProperty, listingUnitName } from '../models/ci-property.model';
import { CiPropertySearchComponent } from '../components/ci-property-search.component';
import { UiCalculatedBoxComponent } from '../ui/ui-calculated-box.component';
import { UiManualInputComponent } from '../ui/ui-manual-input.component';
import { UiMetaStripComponent } from '../ui/ui-meta-strip.component';
import { UiNoteBarComponent } from '../ui/ui-note-bar.component';
import { UiReadonlyFieldComponent } from '../ui/ui-readonly-field.component';
import { formatCurrency, formatNumber, formatPsf } from '../../../core/utils/format.util';

/**
 * Step 1 takes four shapes.
 *
 * Units (primary, resale) show inventory-sourced size and price with a derived
 * PSF. Land and houses show only name, market and cluster from inventory —
 * plot area and asking price stay editable when seeded from inventory.
 */
@Component({
  selector: 'ci-step-property',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    CiPropertySearchComponent,
    UiReadonlyFieldComponent,
    UiCalculatedBoxComponent,
    UiManualInputComponent,
    UiMetaStripComponent,
    UiNoteBarComponent,
  ],
  templateUrl: './step-property.component.html',
})
export class StepPropertyComponent {
  readonly form = input.required<PropertyForm>();
  readonly category = input.required<PropertyCategory>();
  /** Seeded from the selected inventory property's calculated PSF for land-led flows. */
  readonly landRateControl = input<FormControl<number | null> | null>(null);
  /** Independent houses inherit their inventory built-up area into Building Value. */
  readonly builtUpAreaControl = input<FormControl<number | null> | null>(null);

  readonly selectedId = signal<string | null>(null);
  /** Fields the selected property record did not supply — shown as inputs. */
  readonly missing = signal({ size: false, price: false, third: false });

  isLandLed(): boolean {
    return isLandLed(this.category());
  }

  isPrimary(): boolean {
    return this.category() === 'primary-project';
  }

  isResale(): boolean {
    return this.category() === 'resale';
  }

  /** Blank for land-led flows — the reference omits CI Level there. */
  ciLevel(): string {
    return this.isLandLed() ? '' : categoryDefinition(this.category()).basis;
  }

  /** "First Listed Configuration / Unit 1" on primary, listing name on resale. */
  thirdFieldLabel(): string {
    return this.isPrimary() ? 'First Listed Configuration / Unit 1' : 'Listing / Unit Name';
  }

  thirdFieldValue(): string {
    const v = this.form().getRawValue();
    return this.isPrimary() ? v.configuration : v.listingUnitName;
  }

  sizeDisplay(): string {
    const size = this.form().getRawValue().size;
    return size ? `${formatNumber(size)} sq.ft` : '—';
  }

  priceDisplay(): string {
    return formatCurrency(this.form().getRawValue().quotedPrice);
  }

  quotedPsf(): string {
    const { size, quotedPrice } = this.form().getRawValue();
    if (!size || !quotedPrice || size <= 0) return '—';
    return formatPsf(quotedPrice / size);
  }

  noteText(): string {
    return this.isPrimary()
      ? 'Primary Project CI is generated at project level using the first listed configuration / Unit 1.'
      : 'Each resale listing is evaluated independently because size, price, condition, floor, view, UDS and rental inputs may differ.';
  }

  applyInventory(item: InventoryProperty): void {
    this.selectedId.set(item.id);
    this.missing.set({
      size: !(this.isLandLed() ? item.landArea : item.size),
      price: !item.quotedPrice,
      third: this.isPrimary() ? !item.configuration : !listingUnitName(item),
    });

    this.form().patchValue({
      propertyId: item.id,
      propertyName: item.propertyName,
      microMarket: item.microMarket,
      cluster: item.cluster,
      configuration: item.configuration,
      listingUnitName: listingUnitName(item),
      // Inventory values seed every property type. Plot and house values stay
      // editable because analysts may need to correct incomplete inventory.
      // Land-led valuation must never use the listing's SBA as plot area.
      size: this.isLandLed() ? item.landArea : item.size,
      quotedPrice: item.quotedPrice,
    });

    // The Circled Land Rate is maintained by the Supply Team. It is never seeded
    // from inventory: averagePsf falls back to asking price ÷ size, which would
    // make the land value equal the asking price.
    if (this.category() === 'independent-house') {
      this.builtUpAreaControl()?.setValue(item.builtUpArea);
    }

    this.form().markAsDirty();
  }
}
